<template>
	<div class="min-h-screen bg-gray-50">
		<div class="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center flex-wrap gap-4">
			<div class="flex flex-col gap-2">
				<router-link to="/themes" class="flex items-center gap-2 text-gray-500 no-underline text-sm hover:text-orange-7">
					<i class="fa fa-arrow-left"></i>
					<span>Back to Themes</span>
				</router-link>
				<h1 class="text-2xl font-semibold text-gray-900 m-0">Edit Theme</h1>
			</div>
			<button
				v-if="!isLoading"
				class="bg-orange-7 text-white border-none rounded-md px-5 py-2.5 text-sm font-medium cursor-pointer hover:bg-orange-6 disabled:opacity-50 disabled:cursor-not-allowed"
				:disabled="isSaving"
				@click="saveTheme"
			>
				{{ isSaving ? 'Saving...' : 'Save Changes' }}
			</button>
		</div>

		<div class="p-6">
			<div v-if="isLoading" class="py-12 px-6 text-center text-gray-500">
				Loading...
			</div>

			<div v-else>
				<!-- Tab Navigation -->
				<div class="flex border-b border-gray-200 mb-6 gap-0">
					<button
						v-for="tab in tabs"
						:key="tab.id"
						:class="[
							'bg-transparent border-none border-b-2 px-6 py-3 text-sm font-medium cursor-pointer',
							activeTab === tab.id ? 'text-orange-7 border-orange-7' : 'text-gray-500 border-transparent hover:text-gray-700'
						]"
						@click="activeTab = tab.id"
					>
						{{ tab.label }}
					</button>
				</div>

				<!-- Properties Tab -->
				<div v-if="activeTab === 'properties'" class="bg-white rounded-lg shadow-sm overflow-hidden">
					<div class="flex justify-between items-center px-6 py-4 border-b border-gray-200">
						<h2 class="text-lg font-semibold text-gray-900 m-0">Theme Properties</h2>
						<span class="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs font-medium">ID: {{ themeId }}</span>
					</div>
					<div class="p-6">
						<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div class="mb-0">
								<label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
								<input
									v-model="theme.name"
									type="text"
									placeholder="Enter theme name"
									class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent"
								>
							</div>

							<div class="mb-0">
								<label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
								<textarea
									v-model="theme.description"
									placeholder="Enter theme description"
									class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border resize-y focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent"
									rows="3"
								></textarea>
							</div>

							<div class="mb-0">
								<label class="block text-sm font-medium text-gray-700 mb-1">Default Layout</label>
								<select v-model="theme.layoutid" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border bg-white focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
									<option :value="null">-- Select Layout --</option>
									<option
										v-for="layout in layouts"
										:key="layout.layoutid"
										:value="layout.layoutid"
									>
										{{ layout.name }}
									</option>
								</select>
							</div>

							<div class="mb-0">
								<label class="block text-sm font-medium text-gray-700 mb-1">Default Page Color</label>
								<div class="flex gap-2">
									<input
										v-model="theme.color"
										type="text"
										placeholder="e.g., #FFFFFF"
										class="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent"
									>
									<input
										v-if="theme.color"
										type="color"
										:value="theme.color"
										@input="theme.color = ($event.target as HTMLInputElement).value"
										class="w-10 h-10 p-0 border border-gray-300 rounded cursor-pointer"
									>
								</div>
							</div>

							<div class="mb-0">
								<label class="block text-sm font-medium text-gray-700 mb-1">Min Photos</label>
								<input
									v-model.number="theme.minphotos"
									type="number"
									min="0"
									class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent"
								>
							</div>

							<div class="mb-0">
								<label class="block text-sm font-medium text-gray-700 mb-1">Max Photos</label>
								<input
									v-model.number="theme.maxphotos"
									type="number"
									min="0"
									class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent"
								>
							</div>

							<div class="mb-0">
								<label class="block text-sm font-medium text-gray-700 mb-1">Serial Number</label>
								<input
									v-model.number="theme.serialnumber"
									type="number"
									class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent"
								>
							</div>

							<div class="md:col-span-2 mb-0">
								<label class="block text-sm font-medium text-gray-700 mb-1">Theme Icon</label>
								<div class="flex items-start gap-4">
									<img
										v-if="theme.icon"
										:src="theme.icon"
										alt="Theme icon"
										class="w-24 h-24 rounded object-contain border border-gray-200"
									>
									<div v-else class="w-24 h-24 bg-gray-100 rounded flex items-center justify-center text-gray-400 text-sm">No icon</div>
									<div class="flex flex-col gap-2">
										<button
											class="bg-gray-100 text-gray-700 border border-gray-300 rounded-md px-4 py-2 text-sm cursor-pointer hover:bg-gray-200"
											@click="uploadIcon"
										>
											{{ theme.icon ? 'Change Icon' : 'Upload Icon' }}
										</button>
										<button
											v-if="theme.icon"
											class="bg-red-50 text-red-600 border-none rounded px-3 py-1.5 text-sm cursor-pointer hover:bg-red-100"
											@click="removeIcon"
										>
											Remove
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Layouts Tab -->
				<div v-if="activeTab === 'layouts'" class="bg-white rounded-lg shadow-sm overflow-hidden">
					<div class="flex justify-between items-center px-6 py-4 border-b border-gray-200">
						<h2 class="text-lg font-semibold text-gray-900 m-0">Linked Layouts</h2>
					</div>
					<div class="overflow-x-auto">
						<table class="w-full border-collapse">
							<thead>
								<tr>
									<th class="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">ID</th>
									<th class="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Name</th>
									<th class="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Actions</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="layout in layouts" :key="layout.layoutid" class="border-b border-gray-200 hover:bg-gray-50">
									<td class="px-6 py-3 text-sm text-gray-700">{{ layout.layoutid }}</td>
									<td class="px-6 py-3 text-sm text-gray-700">{{ layout.name }}</td>
									<td class="px-6 py-3 text-sm text-gray-700">
										<button
											class="bg-transparent border border-gray-300 rounded p-1.5 cursor-pointer text-gray-500 hover:bg-gray-100 hover:text-gray-700"
											title="View Layout"
											@click="viewLayout(layout.layoutid)"
										>
											<i class="fa fa-eye"></i>
										</button>
									</td>
								</tr>
								<tr v-if="layouts.length === 0">
									<td class="px-6 py-3 text-sm text-gray-700" colspan="3">
										<div class="py-12 px-6 text-center text-gray-500">No layouts linked to this theme</div>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>

				<!-- Categories Tab -->
				<div v-if="activeTab === 'categories'" class="bg-white rounded-lg shadow-sm overflow-hidden">
					<div class="flex justify-between items-center px-6 py-4 border-b border-gray-200">
						<h2 class="text-lg font-semibold text-gray-900 m-0">Linked Categories</h2>
					</div>
					<div class="overflow-x-auto">
						<table class="w-full border-collapse">
							<thead>
								<tr>
									<th class="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">ID</th>
									<th class="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Name</th>
									<th class="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Actions</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="category in categories" :key="category.id" class="border-b border-gray-200 hover:bg-gray-50">
									<td class="px-6 py-3 text-sm text-gray-700">{{ category.id }}</td>
									<td class="px-6 py-3 text-sm text-gray-700">{{ category.name }}</td>
									<td class="px-6 py-3 text-sm text-gray-700">
										<button
											class="bg-transparent border border-gray-300 rounded p-1.5 cursor-pointer text-gray-500 hover:bg-gray-100 hover:text-gray-700"
											title="View Category"
											@click="viewCategory(category.id)"
										>
											<i class="fa fa-eye"></i>
										</button>
									</td>
								</tr>
								<tr v-if="categories.length === 0">
									<td class="px-6 py-3 text-sm text-gray-700" colspan="3">
										<div class="py-12 px-6 text-center text-gray-500">No categories linked to this theme</div>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>

				<!-- Mobile Save Button -->
				<div class="hidden py-4">
					<button
						class="w-full bg-orange-7 text-white border-none rounded-md px-5 py-2.5 text-sm font-medium cursor-pointer hover:bg-orange-6 disabled:opacity-50 disabled:cursor-not-allowed"
						:disabled="isSaving"
						@click="saveTheme"
					>
						{{ isSaving ? 'Saving...' : 'Save Changes' }}
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, getCurrentInstance } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ThemeModel, LayoutModel, ThemeCategoryModel } from '@sosocio/creator/interfaces';
import Swal from 'sweetalert2';
import ThemeService from 'services/ThemeService';
import { httpClient } from 'utils/http';

export default defineComponent({
	name: 'EditThemePage',
	setup() {
		const route = useRoute();
		const router = useRouter();
		const instance = getCurrentInstance();

		const themeId = route.params.id as string;

		const theme = ref<Partial<ThemeModel>>({});
		const layouts = ref<LayoutModel[]>([]);
		const categories = ref<ThemeCategoryModel[]>([]);
		const isLoading = ref(true);
		const isSaving = ref(false);
		const activeTab = ref('properties');

		const tabs = [
			{ id: 'properties', label: 'Properties' },
			{ id: 'layouts', label: 'Layouts' },
			{ id: 'categories', label: 'Categories' },
		];

		const loadTheme = async () => {
			isLoading.value = true;
			try {
				const [themeData, layoutsData, categoriesData] = await Promise.all([
					ThemeService.get(themeId),
					httpClient.get<LayoutModel[]>(`/api/theme/${themeId}/layouts`),
					ThemeService.getAllThemeCategories(themeId),
				]);
				theme.value = themeData;
				layouts.value = layoutsData.data;
				categories.value = categoriesData;
			} catch (error: any) {
				console.error('Failed to load theme:', error);
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to load theme',
					icon: 'error',
				});
			} finally {
				isLoading.value = false;
			}
		};

		const saveTheme = async () => {
			isSaving.value = true;
			try {
				await ThemeService.update(themeId, theme.value);
				Swal.fire({
					toast: true,
					position: 'top-end',
					icon: 'success',
					title: 'Theme saved successfully',
					showConfirmButton: false,
					timer: 3000,
				});
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to save theme',
					icon: 'error',
				});
			} finally {
				isSaving.value = false;
			}
		};

		const uploadIcon = () => {
			const client = instance?.appContext.config.globalProperties.$client;
			if (!client) {
				Swal.fire({
					title: 'Error',
					text: 'File upload service is not available',
					icon: 'error',
				});
				return;
			}

			client.picker({
				fromSources: ['local_file_system'],
				onUploadDone: async (files: any) => {
					if (files.filesUploaded.length > 0) {
						theme.value.icon = files.filesUploaded[0].url;
					}
				},
			}).open();
		};

		const removeIcon = () => {
			theme.value.icon = '';
		};

		const viewLayout = (layoutId: number) => {
			router.push(`/layout/${layoutId}`);
		};

		const viewCategory = (categoryId: number) => {
			router.push(`/themecategory/${categoryId}`);
		};

		onMounted(() => {
			loadTheme();
		});

		return {
			themeId,
			theme,
			layouts,
			categories,
			isLoading,
			isSaving,
			activeTab,
			tabs,
			saveTheme,
			uploadIcon,
			removeIcon,
			viewLayout,
			viewCategory,
		};
	},
});
</script>
